import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, MegaphoneSimple, Sparkle, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGES = [
  { value: "nuevo", label: "Nuevos" },
  { value: "contactado", label: "Contactados" },
  { value: "calificado", label: "Calificados" },
  { value: "negociacion", label: "En negociacion" },
  { value: "ganado", label: "Ganados" },
  { value: "perdido", label: "Perdidos" },
];

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", message: "", target_stages: [], target_tags: [] });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ objective: "", audience: "", tone: "amigable y persuasivo", include_emoji: true });
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/campaigns");
    setCampaigns(data);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.message) return toast.error("Nombre y mensaje requeridos");
    try {
      await api.post("/campaigns", form);
      toast.success("Campana creada");
      setOpen(false);
      setForm({ name: "", message: "", target_stages: [], target_tags: [] });
      load();
    } catch {
      toast.error("Error al crear");
    }
  };

  const send = async (id) => {
    if (!confirm("Enviar esta campana ahora a todos los contactos objetivo?")) return;
    try {
      const { data } = await api.post(`/campaigns/${id}/send`);
      toast.success(`Enviada a ${data.sent}/${data.targets} contactos`);
      load();
    } catch {
      toast.error("Error al enviar");
    }
  };

  const del = async (id) => {
    if (!confirm("Eliminar campana?")) return;
    await api.delete(`/campaigns/${id}`);
    load();
  };

  const generateAI = async () => {
    if (!aiForm.objective) return toast.error("Describe el objetivo");
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-campaign", aiForm);
      setForm({ ...form, name: data.name, message: data.message });
      setAiOpen(false);
      setOpen(true);
      toast.success("Campana generada con IA");
    } catch {
      toast.error("Error en IA");
    } finally {
      setGenerating(false);
    }
  };

  const toggleStage = (s) => {
    const exists = form.target_stages.includes(s);
    setForm({ ...form, target_stages: exists ? form.target_stages.filter((x) => x !== s) : [...form.target_stages, s] });
  };

  return (
    <div className="p-8 max-w-6xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2">Marketing</div>
          <h1 className="font-display text-4xl font-medium tracking-tighter">Campanas</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Envia promociones y recordatorios masivos.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={aiOpen} onOpenChange={setAiOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#27272A] bg-[#121214] hover:bg-[#18181B] text-[#9EA9FF]" data-testid="ai-campaign-button">
                <Sparkle size={14} className="mr-1.5" weight="fill" /> Generar con IA
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121214] border-[#27272A] text-white">
              <DialogHeader><DialogTitle className="font-display tracking-tight">Generar campana con IA</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Objetivo</Label><Textarea value={aiForm.objective} onChange={(e) => setAiForm({ ...aiForm, objective: e.target.value })} placeholder="Ej: promover 20% de descuento en plan anual" className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs" data-testid="ai-objective" /></div>
                <div><Label>Audiencia</Label><Input value={aiForm.audience} onChange={(e) => setAiForm({ ...aiForm, audience: e.target.value })} placeholder="Ej: dueños de tiendas online" className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="ai-audience" /></div>
                <div><Label>Tono</Label>
                  <Select value={aiForm.tone} onValueChange={(v) => setAiForm({ ...aiForm, tone: v })}>
                    <SelectTrigger className="bg-[#0A0A0C] border-[#27272A] mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121214] border-[#27272A]">
                      <SelectItem value="amigable y persuasivo">Amigable y persuasivo</SelectItem>
                      <SelectItem value="formal y directo">Formal y directo</SelectItem>
                      <SelectItem value="casual y divertido">Casual y divertido</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={generateAI} disabled={generating} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="ai-generate-submit">
                  {generating ? "Generando..." : "Generar mensaje"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="new-campaign-button">
                <Plus size={16} className="mr-1.5" weight="bold" /> Nueva campana
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121214] border-[#27272A] text-white max-w-lg">
              <DialogHeader><DialogTitle className="font-display tracking-tight">Nueva campana</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="campaign-name" /></div>
                <div>
                  <Label>Mensaje (usa {"{nombre}"} para personalizar)</Label>
                  <Textarea
                    value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Hola {nombre}! Solo hoy 20% OFF..."
                    rows={5}
                    className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs"
                    data-testid="campaign-message"
                  />
                  <div className="text-[10px] text-[#71717A] mt-1">{form.message.length} caracteres</div>
                </div>
                <div>
                  <Label>Etapas objetivo (vacio = todos)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {STAGES.map((s) => (
                      <label key={s.value} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={form.target_stages.includes(s.value)}
                          onCheckedChange={() => toggleStage(s.value)}
                          data-testid={`stage-checkbox-${s.value}`}
                        />
                        <span>{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button onClick={create} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="save-campaign-button">Guardar campana</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {campaigns.length === 0 && (
          <div className="md:col-span-2 border border-dashed border-[#27272A] rounded-lg p-12 text-center text-[#71717A] text-sm">
            <MegaphoneSimple size={32} className="mx-auto mb-3 text-[#3F3F46]" />
            Aun no hay campanas. Crea una para empezar.
          </div>
        )}
        {campaigns.map((c) => (
          <div key={c.id} className="border border-[#27272A] bg-[#121214] rounded-lg p-5 hover:border-[#3F3F46] transition-colors" data-testid={`campaign-${c.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display text-lg font-medium tracking-tight">{c.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#71717A] mt-0.5">{c.status}</div>
              </div>
              <button onClick={() => del(c.id)} className="p-1.5 rounded hover:bg-[#27272A] text-[#71717A] hover:text-[#EF4444]"><Trash size={14} /></button>
            </div>
            <div className="text-xs text-[#A1A1AA] mb-4 line-clamp-3 bg-[#0A0A0C] p-3 rounded border border-[#27272A] font-mono-jb">{c.message}</div>
            <div className="flex items-center justify-between text-xs text-[#71717A]">
              <span>{c.target_count || 0} objetivos · {c.sent_count} enviados</span>
              <Button size="sm" onClick={() => send(c.id)} className="h-7 bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid={`send-${c.id}`}>
                <PaperPlaneTilt size={12} className="mr-1" weight="fill" /> Enviar
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
